import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Envelope,
  Plus,
  Copy,
  Trash,
  Star,
  PencilSimple,
  MagnifyingGlass,
  Shield,
  StackSimple,
} from '@phosphor-icons/react'
import { motion } from 'framer-motion'
import { Card, CardContent } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { Select } from '@/components/ui/Select'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { Tooltip } from '@/components/ui/Tooltip'
import { useTemplateStore } from '@/stores/templateStore'
import { usePermission } from '@/hooks/usePermission'
import { TemplateListItem, TemplateCategory } from '@/types/template'
import { cn } from '@/lib/utils'
import { handleTabListKeyDown } from '@/lib/rovingTabs'
import { toast } from 'sonner'

const CATEGORY_LABELS: Record<TemplateCategory, string> = {
  cold_outreach: 'Cold Outreach',
  follow_up: 'Follow Up',
  nurture: 'Nurture',
  re_engage: 'Re-engage',
  introduction: 'Introduction',
  general: 'General',
}

// Editorial Dark §11: no new chromatic colors. Single neutral pill style
// across all categories — differentiation lives in the label text.
const NEUTRAL_CHIP =
  'bg-muted/60 text-muted-foreground border border-border/50 dark:bg-white/[0.04] dark:border-white/[0.06] dark:text-[#8a8f98]'

const CATEGORY_CHIP: Record<TemplateCategory, string> = {
  cold_outreach: NEUTRAL_CHIP,
  follow_up: NEUTRAL_CHIP,
  nurture: NEUTRAL_CHIP,
  re_engage: NEUTRAL_CHIP,
  introduction: NEUTRAL_CHIP,
  general: NEUTRAL_CHIP,
}

type FilterTab = 'all' | 'custom' | 'system'

const FILTER_TABS: FilterTab[] = ['all', 'custom', 'system']

const EmailTemplateList: React.FC = () => {
  const navigate = useNavigate()
  // Analyst holds mail_templates.manage = 'view' (AB#22340): templates are
  // content, and every Analyst content cell in the spec is View. Create,
  // Duplicate, Set-as-default and Delete all 403 for them server-side, so
  // don't offer them; the list and the read-only editor stay.
  const { canWrite } = usePermission('mail_templates', 'manage')
  const { templates, isLoading, fetchTemplates, deleteTemplate, duplicateTemplate, setDefaultTemplate } = useTemplateStore()
  const [searchQuery, setSearchQuery] = useState('')
  const [filterTab, setFilterTab] = useState<FilterTab>('all')
  const [categoryFilter, setCategoryFilter] = useState<TemplateCategory | ''>('')
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  useEffect(() => {
    fetchTemplates()
  }, [fetchTemplates])

  const handleDelete = async (id: number) => {
    setIsDeleting(true)
    try {
      await deleteTemplate(id)
      setDeleteConfirmId(null)
    } catch {
      toast.error('Failed to delete template')
    } finally {
      setIsDeleting(false)
    }
  }

  const handleDuplicate = async (id: number) => {
    try {
      await duplicateTemplate(id)
    } catch {
      toast.error('Failed to duplicate template')
    }
  }

  const handleSetDefault = async (id: number) => {
    try {
      await setDefaultTemplate(id)
    } catch {
      toast.error('Failed to set default template')
    }
  }

  const filteredTemplates = templates.filter((t: TemplateListItem) => {
    if (searchQuery && !t.name.toLowerCase().includes(searchQuery.toLowerCase())) return false
    if (filterTab === 'custom' && t.is_system) return false
    if (filterTab === 'system' && !t.is_system) return false
    if (categoryFilter && t.category !== categoryFilter) return false
    return true
  })

  if (isLoading && templates.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="space-y-5">
      {/* Filters */}
      <div className="flex flex-col lg:flex-row gap-3 lg:items-center">
        <div className="flex-1 max-w-xl">
          <Input
            placeholder="Search templates..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            icon={<MagnifyingGlass className="h-4 w-4" weight="regular" />}
          />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div
            className="inline-flex p-1 gap-1 rounded-md bg-muted/40 border border-border/50 dark:bg-white/[0.03] dark:border-white/[0.06]"
            role="tablist"
            aria-label="Template source"
            onKeyDown={(e) =>
              handleTabListKeyDown(e, {
                activeIndex: FILTER_TABS.indexOf(filterTab),
                count: FILTER_TABS.length,
                onSelect: (i) => setFilterTab(FILTER_TABS[i]),
              })
            }
          >
            {FILTER_TABS.map((tab) => (
              <button
                key={tab}
                type="button"
                role="tab"
                id={`template-source-tab-${tab}`}
                aria-controls="template-source-panel"
                aria-selected={filterTab === tab}
                tabIndex={filterTab === tab ? 0 : -1}
                className={cn(
                  'px-3 py-1 rounded border transition-colors duration-150',
                  filterTab === tab
                    ? 'bg-background text-foreground border-border/60 shadow-sm dark:bg-white/[0.06] dark:text-[#f7f8f8] dark:border-white/[0.10] dark:shadow-none'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-background/60 dark:text-[#8a8f98] dark:hover:text-[#f7f8f8] dark:hover:bg-white/[0.03]',
                )}
                style={{
                  fontSize: '12px',
                  fontWeight: filterTab === tab ? 590 : 510,
                  letterSpacing: '-0.13px',
                }}
                onClick={() => setFilterTab(tab)}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>
          <Select
            value={categoryFilter}
            onChange={(v) => setCategoryFilter(v as TemplateCategory | '')}
            ariaLabel="Filter by category"
            minWidth={170}
            options={[
              { value: '', label: 'All categories' },
              ...Object.entries(CATEGORY_LABELS).map(([value, label]) => ({ value, label })),
            ]}
          />
        </div>
        {canWrite && (
          <Button
            variant="default"
            size="sm"
            icon={<Plus className="h-4 w-4" weight="regular" />}
            onClick={() => navigate('/templates/new')}
            className="lg:ml-auto"
          >
            Create template
          </Button>
        )}
      </div>

      {/* Templates grid. Both branches carry the tabpanel identity — only one
          renders at a time, so the id stays unique. */}
      {filteredTemplates.length === 0 ? (
        <Card
          variant="default"
          className="rounded-lg border-border/50 dark:bg-white/[0.02] dark:border-white/[0.08]"
          role="tabpanel"
          id="template-source-panel"
          aria-labelledby={`template-source-tab-${filterTab}`}
        >
          <CardContent className="p-12 text-center">
            <div className="max-w-md mx-auto">
              <div className="h-12 w-12 rounded-md bg-muted/70 dark:bg-white/[0.04] dark:border dark:border-white/[0.05] flex items-center justify-center mx-auto mb-4">
                <Envelope className="h-5 w-5 text-muted-foreground dark:text-[#8a8f98]" weight="regular" />
              </div>
              <h3
                className="text-foreground dark:text-[#f7f8f8] mb-2"
                style={{
                  fontSize: '20px',
                  fontWeight: 590,
                  lineHeight: 1.33,
                  letterSpacing: '-0.24px',
                }}
              >
                {searchQuery || categoryFilter || filterTab !== 'all' ? 'No templates found' : 'No templates yet'}
              </h3>
              <p
                className="text-muted-foreground dark:text-[#8a8f98] mb-6"
                style={{ fontSize: '13px', letterSpacing: '-0.13px', lineHeight: 1.5 }}
              >
                {searchQuery || categoryFilter || filterTab !== 'all'
                  // Name the filters that are actually on. "Try adjusting your
                  // filters" left people hunting for which one was hiding
                  // everything — the tab row and the category dropdown both
                  // have an "All", so it reads as though one should have
                  // cleared the other (AB#22325). It does not: the tabs filter
                  // by TYPE (custom vs system), the dropdown by CATEGORY.
                  ? `No templates match ${[
                      searchQuery && `"${searchQuery}"`,
                      categoryFilter && CATEGORY_LABELS[categoryFilter as TemplateCategory],
                      filterTab !== 'all' && `${filterTab} templates`,
                    ].filter(Boolean).join(' + ')}.`
                  : canWrite
                    ? 'Create your first email template to define how your emails are structured.'
                    : 'Templates your team creates will show up here.'
                }
              </p>
              {(searchQuery || categoryFilter || filterTab !== 'all') && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => { setSearchQuery(''); setCategoryFilter(''); setFilterTab('all') }}
                >
                  Clear filters
                </Button>
              )}
              {!searchQuery && !categoryFilter && filterTab === 'all' && canWrite && (
                <Button variant="default" size="sm" onClick={() => navigate('/templates/new')}>
                  <Plus className="h-4 w-4 mr-2" weight="regular" />
                  Create your first template
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      ) : (
        <div
          className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4"
          role="tabpanel"
          id="template-source-panel"
          aria-labelledby={`template-source-tab-${filterTab}`}
        >
          {filteredTemplates.map((template: TemplateListItem) => (
            <motion.div
              key={template.id}
              whileHover={{ y: -2 }}
              transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
            >
              <Card
                variant="default"
                className="relative h-full rounded-lg border-border/50 transition-colors duration-200 hover:border-border/80 dark:bg-white/[0.02] dark:border-white/[0.08] dark:hover:bg-white/[0.04] dark:hover:border-[#7170ff]/25"
              >
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1.5">
                        <h3
                          className="text-foreground dark:text-[#f7f8f8] truncate"
                          style={{ fontSize: '15px', fontWeight: 510, letterSpacing: '-0.182px', lineHeight: 1.3 }}
                        >
                          {template.name}
                        </h3>
                        {template.is_default && (
                          <Star className="h-4 w-4 text-[#f5a524] flex-shrink-0" weight="fill" aria-label="Default template" />
                        )}
                      </div>
                      {template.description && (
                        <p
                          className="text-muted-foreground dark:text-[#8a8f98] line-clamp-2"
                          style={{ fontSize: '13px', letterSpacing: '-0.13px', lineHeight: 1.5 }}
                        >
                          {template.description}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-1.5 mb-4">
                    <span
                      className={cn(
                        'inline-flex items-center px-2 py-0.5 rounded-full',
                        CATEGORY_CHIP[template.category],
                      )}
                      style={{ fontSize: '11px', fontWeight: 510, letterSpacing: '-0.1px' }}
                    >
                      {CATEGORY_LABELS[template.category]}
                    </span>
                    <span
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-muted/60 text-muted-foreground dark:bg-white/[0.04] dark:border dark:border-white/[0.06] dark:text-[#8a8f98]"
                      style={{ fontSize: '11px', fontWeight: 510, letterSpacing: '-0.1px' }}
                    >
                      <StackSimple className="h-3 w-3" weight="regular" />
                      {template.block_count} block{template.block_count !== 1 ? 's' : ''}
                    </span>
                    {template.is_system && (
                      <span
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 dark:bg-[#5e6ad2]/[0.12] dark:border dark:border-[#5e6ad2]/25 dark:text-[#a5b4fc]"
                        style={{ fontSize: '11px', fontWeight: 510, letterSpacing: '-0.1px' }}
                      >
                        <Shield className="h-3 w-3" weight="regular" />
                        System
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-1.5 pt-3 border-t border-border/50 dark:border-white/[0.06]">
                    <Button
                      variant="outline"
                      size="sm"
                      className="hover:border-[#5e6ad2]/40 hover:bg-[#5e6ad2]/[0.05] hover:text-[#5e6ad2] dark:hover:border-[#5e6ad2]/40 dark:hover:bg-[#5e6ad2]/[0.08] dark:hover:text-[#a5b4fc]"
                      onClick={() => navigate(`/templates/${template.id}/edit`)}
                    >
                      <PencilSimple className="h-3.5 w-3.5 mr-1.5" weight="regular" />
                      {template.is_system || !canWrite ? 'View' : 'Edit'}
                    </Button>
                    {canWrite && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="hover:border-[#5e6ad2]/40 hover:bg-[#5e6ad2]/[0.05] hover:text-[#5e6ad2] dark:hover:border-[#5e6ad2]/40 dark:hover:bg-[#5e6ad2]/[0.08] dark:hover:text-[#a5b4fc]"
                        onClick={() => handleDuplicate(template.id)}
                      >
                        <Copy className="h-3.5 w-3.5 mr-1.5" weight="regular" />
                        Duplicate
                      </Button>
                    )}
                    {canWrite && !template.is_default && (
                      <Tooltip label="Set as default">
                        <Button variant="ghost" size="sm" onClick={() => handleSetDefault(template.id)} aria-label="Set as default">
                          <Star className="h-3.5 w-3.5" weight="regular" />
                        </Button>
                      </Tooltip>
                    )}
                    {canWrite && !template.is_system && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="ml-auto text-muted-foreground hover:text-destructive dark:text-[#8a8f98] dark:hover:text-red-400"
                        onClick={() => setDeleteConfirmId(template.id)}
                        aria-label="Delete template"
                      >
                        <Trash className="h-3.5 w-3.5" weight="regular" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      )}

      <ConfirmDialog
        open={deleteConfirmId !== null}
        title="Delete template"
        description="Are you sure you want to delete this template? This action cannot be undone."
        confirmLabel="Delete"
        cancelLabel="Cancel"
        tone="destructive"
        loading={isDeleting}
        onConfirm={() => deleteConfirmId !== null && handleDelete(deleteConfirmId)}
        onCancel={() => !isDeleting && setDeleteConfirmId(null)}
      />
    </div>
  )
}

export default EmailTemplateList
